import React, { useCallback, useMemo, useState } from "react";
import { MAX_COUNTERPARTY_ADDRESSES_PER_COUNTERPARTY } from "@common/constants/counterparty";
import {
    CounterpartyAddress,
    CreateCounterpartyAddressBody,
} from "@common/schemas/counterpartyAddress.schema";
import { View } from "@common/types/views/views";
import { CounterpartyAddressesViewSchema } from "@common/types/views/counterpartyAddressesView";
import {
    useCreateCounterpartyAddressMutation,
    useDeleteCounterpartyAddressMutation,
    useSetPrimaryCounterpartyAddressMutation,
    useUpdateCounterpartyAddressMutation,
} from "@api/counterparty.api";
import { isViewOnlyRefusalError } from "@lib/viewOnlyRefusal";
import { Button } from "@design-system/Button/Button";
import { ConfirmationModal } from "@design-system/Modal/ConfirmationModal";
import { useToaster } from "@design-system/ToastMessages/useToaster";
import { ViewTable } from "@design-system/ViewTable/ViewTable";
import type { ViewTableMeta } from "@design-system/ViewTable/ViewTable.lib";
import formStyles from "@components/counterparty/CounterpartyForm.module.scss";
import { formatCounterpartyAddressLines } from "@components/counterparty/counterpartyBookFormat";
import { TablePageHeader } from "@components/pageHeader/TablePageHeader";
import { CounterpartyAddressModal } from "./CounterpartyAddressModal";
import {
    CounterpartyBookTableCallbacks,
    CounterpartyBookTableOptions,
} from "./counterpartyBookTableMeta";
import { createCounterpartyAddressesView } from "./createCounterpartyBookViews";
import { createCounterpartyAddressesViewConfig } from "./counterpartyAddressesViewConfig";
import { matchesCounterpartyBookSearch } from "./matchesCounterpartyBookSearch";
import { preventSearchImplicitSubmit } from "./preventSearchImplicitSubmit";
import { isOcrSeedId } from "./ocrSeedRow";
import { LocalBookHandlers } from "./useOcrSeedBooks";
import { useCounterpartyBookEditor } from "./useCounterpartyBookEditor";
import styles from "./CounterpartyEditorDrawer.module.scss";

interface Props {
    counterpartyId: string;
    addresses: CounterpartyAddress[];
    isReadOnly?: boolean;
    resolveWritableCounterpartyId?: () => Promise<string>;
    resolveWritableAddress?: (
        address: CounterpartyAddress,
    ) => Promise<{ counterpartyId: string; addressId: string }>;
    onEditingChange?: (isEditing: boolean) => void;
    localBook?: LocalBookHandlers<CreateCounterpartyAddressBody>;
    suggestedIds?: readonly string[];
    removedIds?: readonly string[];
}

export const CounterpartyAddressesSection: React.FC<Props> = ({
    counterpartyId,
    addresses,
    isReadOnly = false,
    resolveWritableCounterpartyId,
    resolveWritableAddress,
    onEditingChange,
    localBook,
    suggestedIds = [],
    removedIds = [],
}) => {
    const toaster = useToaster();
    const [search, setSearch] = useState("");
    const [view, setView] = useState(createCounterpartyAddressesView);
    const {
        editingRow: editingAddress,
        isModalOpen,
        deletingRow: deletingAddress,
        closeModal,
        startAdd,
        startEdit,
        startDelete,
        clearDeleting,
    } = useCounterpartyBookEditor({
        rows: addresses,
        onEditingChange,
    });
    const viewConfig = useMemo(
        () => createCounterpartyAddressesViewConfig(),
        [],
    );

    const createAddress = useCreateCounterpartyAddressMutation();
    const updateAddress = useUpdateCounterpartyAddressMutation();
    const deleteAddress = useDeleteCounterpartyAddressMutation();
    const setPrimary = useSetPrimaryCounterpartyAddressMutation();
    const resolveCounterpartyId = useCallback(
        () =>
            resolveWritableCounterpartyId
                ? resolveWritableCounterpartyId()
                : Promise.resolve(counterpartyId),
        [counterpartyId, resolveWritableCounterpartyId],
    );
    const resolveAddress = useCallback(
        (address: CounterpartyAddress) =>
            resolveWritableAddress
                ? resolveWritableAddress(address)
                : Promise.resolve({ counterpartyId, addressId: address.id }),
        [counterpartyId, resolveWritableAddress],
    );

    const filteredAddresses = useMemo(
        () =>
            addresses.filter((address) =>
                matchesCounterpartyBookSearch(
                    [...formatCounterpartyAddressLines(address), address.label],
                    search,
                ),
            ),
        [addresses, search],
    );

    const tableMeta = useMemo<
        ViewTableMeta<
            CounterpartyBookTableCallbacks,
            CounterpartyBookTableOptions
        >
    >(
        () => ({
            callbacks: {
                onEdit: startEdit,
                onDelete: startDelete,
                onMakePrimary: (id) => {
                    const address = addresses.find((row) => row.id === id);
                    if (!address) {
                        return;
                    }
                    if (isOcrSeedId(address.id)) {
                        localBook?.makePrimary(address.id);
                        return;
                    }
                    void resolveAddress(address)
                        .then(({ counterpartyId: writableId, addressId }) =>
                            setPrimary.mutateAsync({
                                counterpartyId: writableId,
                                addressId,
                            }),
                        )
                        .catch((error) => {
                            if (!isViewOnlyRefusalError(error)) {
                                toaster.error(
                                    "Failed to make the address primary",
                                );
                            }
                        });
                },
            },
            options: {
                isReadOnly,
                isDisabled: false,
                removedIds,
            },
        }),
        [
            addresses,
            isReadOnly,
            removedIds,
            resolveAddress,
            setPrimary,
            startDelete,
            startEdit,
            toaster,
            localBook,
        ],
    );

    const handleViewChange = useCallback((next: View) => {
        setView(CounterpartyAddressesViewSchema.parse(next));
    }, []);

    const canAdd =
        !isReadOnly &&
        addresses.length < MAX_COUNTERPARTY_ADDRESSES_PER_COUNTERPARTY;

    return (
        <>
            <section
                className={formStyles.section}
                onKeyDown={preventSearchImplicitSubmit}
            >
                <h4 className={styles.bookSectionTitle}>Addresses</h4>
                <TablePageHeader
                    search={search}
                    onSearchChange={setSearch}
                    searchPlaceholder="Search addresses..."
                    searchKeyboardShortcuts={false}
                >
                    {!isReadOnly && (
                        <Button
                            type="button"
                            variant="primary"
                            size="md"
                            onClick={startAdd}
                            disabled={!canAdd}
                        >
                            Add address
                        </Button>
                    )}
                </TablePageHeader>
                <ViewTable
                    data={filteredAddresses}
                    view={view}
                    config={viewConfig}
                    onViewChange={handleViewChange}
                    tableMeta={tableMeta}
                    emptyView={() => (
                        <p className={styles.emptyState}>
                            {search
                                ? "No matching addresses"
                                : "No addresses yet"}
                        </p>
                    )}
                    enableKeyboardShortcuts={false}
                    rowBorders={false}
                    outerBorder
                    className={styles.bookTable}
                    getRowClassName={(address: CounterpartyAddress) => {
                        if (removedIds.includes(address.id)) {
                            return styles.replacedRow;
                        }
                        if (suggestedIds.includes(address.id)) {
                            return styles.suggestedRow;
                        }
                        return undefined;
                    }}
                />
            </section>
            <CounterpartyAddressModal
                show={isModalOpen}
                address={editingAddress}
                onHide={closeModal}
                onSubmit={async (body) => {
                    if (editingAddress && isOcrSeedId(editingAddress.id)) {
                        localBook?.save(editingAddress.id, body);
                        closeModal();
                        return;
                    }
                    if (editingAddress) {
                        const { counterpartyId: writableId, addressId } =
                            await resolveAddress(editingAddress);
                        await updateAddress.mutateAsync({
                            counterpartyId: writableId,
                            addressId,
                            body,
                        });
                    } else {
                        const writableId = await resolveCounterpartyId();
                        await createAddress.mutateAsync({
                            counterpartyId: writableId,
                            body,
                        });
                    }
                    closeModal();
                }}
            />
            <ConfirmationModal
                show={deletingAddress !== null}
                title="Delete address"
                question={
                    deletingAddress?.isPrimary
                        ? "Delete this primary address? The next-oldest address becomes primary."
                        : "Delete this address?"
                }
                yes="Delete"
                yesBtnVariant="destructive"
                onConfirm={async () => {
                    if (!deletingAddress) {
                        return;
                    }
                    if (isOcrSeedId(deletingAddress.id)) {
                        localBook?.remove(deletingAddress.id);
                        clearDeleting();
                        return;
                    }
                    const { counterpartyId: writableId, addressId } =
                        await resolveAddress(deletingAddress);
                    await deleteAddress.mutateAsync({
                        counterpartyId: writableId,
                        addressId,
                    });
                    clearDeleting();
                }}
                onHide={clearDeleting}
            />
        </>
    );
};
